import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { RESCHEDULE_SR_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  RescheduleErrorKind,
  RescheduleServiceRequestPayload,
  RescheduleServiceRequestResponseData,
  RescheduleServiceRequestResult,
} from '../types/rescheduleServiceRequestType';
import serviceRequestEndpoints from './serviceRequestEndpoints';

interface MappedError {
  message: string;
  errorKind: RescheduleErrorKind;
}

const mapErrorBody = (
  errorData: unknown,
  status?: number
): MappedError | null => {
  if (status === 401 || status === 403) {
    return {
      message: RESCHEDULE_SR_ERROR_MESSAGES.UNAUTHORIZED,
      errorKind: 'GENERIC',
    };
  }
  if (status === 500) {
    return {
      message: RESCHEDULE_SR_ERROR_MESSAGES.INTERNAL_SERVER_ERROR,
      errorKind: 'GENERIC',
    };
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    const raw =
      typeof obj.error === 'string' && obj.error.trim().length > 0
        ? obj.error
        : typeof obj.message === 'string' && obj.message.trim().length > 0
          ? obj.message
          : '';
    if (raw) {
      if (status === 409 && /status changed/i.test(raw)) {
        return {
          message: RESCHEDULE_SR_ERROR_MESSAGES.STATUS_CHANGED,
          errorKind: 'STATUS_CHANGED',
        };
      }
      if (status === 409 && /unavailable|conflicting/i.test(raw)) {
        return {
          message: RESCHEDULE_SR_ERROR_MESSAGES.TECHNICIAN_CONFLICT,
          errorKind: 'TECHNICIAN_CONFLICT',
        };
      }
      if (status === 400 && /within 24 hours/i.test(raw)) {
        return {
          message: RESCHEDULE_SR_ERROR_MESSAGES.WITHIN_24H,
          errorKind: 'WITHIN_24H',
        };
      }
      if (status === 400 && /cannot be rescheduled from status/i.test(raw)) {
        return {
          message: RESCHEDULE_SR_ERROR_MESSAGES.NOT_RESCHEDULABLE,
          errorKind: 'NOT_RESCHEDULABLE',
        };
      }
      return { message: raw, errorKind: 'GENERIC' };
    }
  }
  return null;
};

const toFailure = (
  errorData: unknown,
  status?: number
): RescheduleServiceRequestResult => {
  const mapped = mapErrorBody(errorData, status);
  return {
    success: false,
    error: mapped?.message ?? RESCHEDULE_SR_ERROR_MESSAGES.DEFAULT_ERROR,
    errorKind: mapped?.errorKind ?? 'GENERIC',
  };
};

export const rescheduleServiceRequest = async ({
  serviceRequestId,
  technicianId,
  startDate,
  endDate,
  startTime,
  endTime,
  adminNotes,
  notify,
}: RescheduleServiceRequestPayload): Promise<RescheduleServiceRequestResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: RESCHEDULE_SR_ERROR_MESSAGES.DEFAULT_ERROR,
      errorKind: 'GENERIC',
    };
  }

  const trimmedNotes = typeof adminNotes === 'string' ? adminNotes.trim() : '';

  const body: Record<string, unknown> = {
    technician_id: technicianId,
    start_date: startDate,
    end_date: endDate,
    // `notify` defaults to false on the BE; we send it explicitly so the
    // intent at the moment of submit is captured rather than inferred.
    notify: Boolean(notify),
  };
  if (startTime) {
    body['start_time'] = startTime;
  }
  if (endTime) {
    body['end_time'] = endTime;
  }
  if (trimmedNotes.length > 0) {
    body['admin_notes'] = trimmedNotes;
  }

  try {
    const result = await apiService.postAPI(
      serviceRequestEndpoints.RESCHEDULE_SERVICE_REQUEST(trimmedId),
      body,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        data?: RescheduleServiceRequestResponseData;
      };
      return {
        success: true,
        ...(payload.data ? { data: payload.data } : {}),
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: RESCHEDULE_SR_ERROR_MESSAGES.NETWORK_ERROR,
        errorKind: 'GENERIC',
      };
    }

    return toFailure(result.data, result.status);
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: RESCHEDULE_SR_ERROR_MESSAGES.NETWORK_ERROR,
          errorKind: 'GENERIC',
        };
      }
      return toFailure(error.response?.data, error.response?.status);
    }
    return {
      success: false,
      error: RESCHEDULE_SR_ERROR_MESSAGES.UNEXPECTED_ERROR,
      errorKind: 'GENERIC',
    };
  }
};
